import { useState } from "react";
import { useCart } from "@/hooks/useCart";

interface OrderSummaryProps {
  shipping: number;
  promoDiscount: number;
  onPromoApply: (discount: number) => void;
}

const PROMO_CODES: Record<string, number> = {
  GLOW10: 10,
  IIFF20: 20,
  WELCOME15: 15,
};

export default function OrderSummary({ shipping, promoDiscount, onPromoApply }: OrderSummaryProps) {
  const { items, total } = useCart();
  const [promoInput, setPromoInput] = useState("");
  const [promoStatus, setPromoStatus] = useState<"idle" | "valid" | "invalid">("idle");
  const [promoApplied, setPromoApplied] = useState("");

  const taxRate = 0.085;
  const subtotal = total;
  const discount = promoDiscount;
  const taxBase = subtotal - discount;
  const tax = taxBase * taxRate;
  const orderTotal = taxBase + shipping + tax;

  const handlePromo = () => {
    const code = promoInput.trim().toUpperCase();
    if (PROMO_CODES[code]) {
      setPromoStatus("valid");
      setPromoApplied(code);
      onPromoApply(PROMO_CODES[code]);
    } else {
      setPromoStatus("invalid");
    }
  };

  const handleRemovePromo = () => {
    setPromoStatus("idle");
    setPromoInput("");
    setPromoApplied("");
    onPromoApply(0);
  };

  return (
    <div className="bg-[#F5F2ED] rounded-lg p-6 sticky top-24" style={{ fontFamily: "'Jost', sans-serif" }}>
      <h3
        className="text-lg font-light text-midnight mb-5"
        style={{ fontFamily: "'Cormorant Garamond', serif" }}
      >
        Order Summary
      </h3>

      {/* Items */}
      <div className="space-y-4 mb-5 max-h-60 overflow-y-auto pr-1">
        {items.map((item) => (
          <div key={item.cartId} className="flex gap-3">
            <div className="relative w-16 h-20 shrink-0 rounded overflow-hidden bg-pearl/30">
              <img
                src={item.image}
                alt={item.name}
                className="w-full h-full object-cover object-top"
              />
              <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-midnight text-moonlight text-[10px] flex items-center justify-center font-medium">
                {item.quantity}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-midnight leading-snug line-clamp-2 mb-1">{item.name}</p>
              <p className="text-[10px] text-midnight/40">{item.size} · {item.color}</p>
              <p className="text-sm font-medium text-midnight mt-1">${(item.price * item.quantity).toFixed(2)}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="border-t border-pearl/50 pt-4 mb-4">
        {/* Promo Code */}
        {promoStatus !== "valid" ? (
          <div className="flex gap-2 mb-4">
            <input
              type="text"
              value={promoInput}
              onChange={(e) => {
                setPromoInput(e.target.value);
                setPromoStatus("idle");
              }}
              placeholder="Promo code"
              className="flex-1 border border-pearl bg-white px-3 py-2 text-xs text-midnight placeholder-midnight/30 focus:outline-none focus:border-midnight/40 transition-colors"
            />
            <button
              onClick={handlePromo}
              className="px-4 py-2 bg-midnight text-moonlight text-[10px] tracking-widest uppercase hover:bg-midnight/90 transition-colors cursor-pointer whitespace-nowrap"
            >
              Apply
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between mb-4 bg-white border border-green-200 px-3 py-2 rounded">
            <div className="flex items-center gap-2">
              <i className="ri-coupon-line text-green-500 text-sm" />
              <span className="text-xs text-green-600 font-medium">{promoApplied} applied</span>
              <span className="text-xs text-green-500">-${promoDiscount.toFixed(2)}</span>
            </div>
            <button
              onClick={handleRemovePromo}
              className="text-midnight/30 hover:text-midnight transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-sm" />
            </button>
          </div>
        )}
        {promoStatus === "invalid" && (
          <p className="text-[10px] text-red-400 mb-3">Invalid promo code. Try GLOW10 or WELCOME15.</p>
        )}

        {/* Cost Breakdown */}
        <div className="space-y-2.5">
          <div className="flex justify-between text-xs text-midnight/60">
            <span>Subtotal</span>
            <span>${subtotal.toFixed(2)}</span>
          </div>
          {discount > 0 && (
            <div className="flex justify-between text-xs text-green-600">
              <span>Promo discount</span>
              <span>-${discount.toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between text-xs text-midnight/60">
            <span>Shipping</span>
            <span>{shipping === 0 ? <span className="text-green-600">Free</span> : `$${shipping.toFixed(2)}`}</span>
          </div>
          <div className="flex justify-between text-xs text-midnight/60">
            <span>Estimated tax (8.5%)</span>
            <span>${tax.toFixed(2)}</span>
          </div>
        </div>
      </div>

      <div className="border-t border-pearl/50 pt-4">
        <div className="flex justify-between items-center">
          <span className="text-sm font-medium text-midnight tracking-wide">Total</span>
          <span className="text-xl font-medium text-midnight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            ${orderTotal.toFixed(2)}
          </span>
        </div>
        <p className="text-[10px] text-midnight/35 mt-1">Including all taxes &amp; fees</p>
      </div>

      {/* Trust badges */}
      <div className="flex items-center justify-center gap-4 mt-5 pt-4 border-t border-pearl/40">
        {[
          { icon: "ri-lock-line", text: "Secure" },
          { icon: "ri-refresh-line", text: "30-day returns" },
          { icon: "ri-customer-service-line", text: "24/7 Support" },
        ].map((b) => (
          <div key={b.text} className="flex flex-col items-center gap-0.5">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className={`${b.icon} text-sm text-midnight/30`} />
            </div>
            <span className="text-[9px] text-midnight/30 text-center whitespace-nowrap">{b.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
